import {
  GuidanceFileType,
  Modality,
} from "@/components/graphql/schema.graphql";
import { StyleCapabilities } from "@/types";

type CapabilitySection = "general" | "referenceImageTypes" | "configuration";

interface CapabilityConfig {
  key: string;
  label: string;
  section?: CapabilitySection;
  getIsSupported: (capabilities: StyleCapabilities) => boolean;
}

const IMAGE_CAPABILITY_CONFIG: CapabilityConfig[] = [
  // General
  {
    key: "tileable",
    label: "Tileable",
    section: "general",
    getIsSupported: (c) => c.tileability === true,
  },
  {
    key: "supportsReferenceImages",
    label: "Reference Images",
    section: "general",
    getIsSupported: (c) =>
      Array.isArray(c.guidanceFileTypes) && c.guidanceFileTypes.length > 0,
  },
  {
    key: "multipleAspectRatios",
    label: "Aspect Ratios",
    section: "general",
    getIsSupported: (c) =>
      (Array.isArray(c.textToOutputAspectRatios) &&
        c.textToOutputAspectRatios.length > 1) ||
      (Array.isArray(c.imageToOutputAspectRatios) &&
        c.imageToOutputAspectRatios.length > 1),
  },
  // Reference Image Types
  {
    key: "styleReference",
    label: "Style Reference",
    section: "referenceImageTypes",
    getIsSupported: (c) => hasGuidanceFileType(c, "IP_ADAPTER"),
  },
  {
    key: "characterPose",
    label: "Character Pose",
    section: "referenceImageTypes",
    getIsSupported: (c) => hasGuidanceFileType(c, "POSE"),
  },
  {
    key: "structure",
    label: "Structure",
    section: "referenceImageTypes",
    getIsSupported: (c) => hasGuidanceFileType(c, "DEPTH"),
  },
  {
    key: "outline",
    label: "Outline",
    section: "referenceImageTypes",
    getIsSupported: (c) => hasGuidanceFileType(c, "CANNY"),
  },
  {
    key: "lineArt",
    label: "Line Art",
    section: "referenceImageTypes",
    getIsSupported: (c) => hasGuidanceFileType(c, "LINEART"),
  },
  // Configuration
  {
    key: "seed",
    label: "Seed",
    section: "configuration",
    getIsSupported: (c) => c.seed === true,
  },
];

const VIDEO_CAPABILITY_CONFIG: CapabilityConfig[] = [
  {
    key: "videoEffects",
    label: "Video Effects",
    getIsSupported: (c) =>
      Array.isArray(c.videoEffectTypes) && c.videoEffectTypes.length > 0,
  },
  {
    key: "generateAudio",
    label: "Generate Audio",
    getIsSupported: (c) => c.generateAudio === true,
  },
  {
    key: "lipsync",
    label: "Lipsync",
    getIsSupported: (c) => c.lipsync === true,
  },
];

const AUDIO_CAPABILITY_CONFIG: CapabilityConfig[] = [
  {
    key: "textToSpeech",
    label: "Text to Speech",
    getIsSupported: (c) => c.textToSpeech === true,
  },
  {
    key: "soundEffect",
    label: "Sound Effect",
    getIsSupported: (c) => c.soundEffect === true,
  },
  {
    key: "autoDuration",
    label: "Auto Duration",
    getIsSupported: (c) => c.autoDuration === true,
  },
];

const THREE_D_CAPABILITY_CONFIG: CapabilityConfig[] = [
  {
    key: "textTo3D",
    label: "Text to 3D",
    getIsSupported: (c) => c.textTo3D === true,
  },
  {
    key: "imageTo3D",
    label: "Image to 3D",
    getIsSupported: (c) => c.imageTo3D === true,
  },
  {
    key: "textures",
    label: "Textures",
    getIsSupported: (c) => c.textures === true,
  },
  {
    key: "faceLimit",
    label: "Face Limit",
    getIsSupported: (c) => typeof c.faceLimit === "number" && c.faceLimit > 0,
  },
  {
    key: "taPose",
    label: "T/A Pose",
    getIsSupported: (c) => (c.poseModes?.length ?? 0) > 0,
  },
  {
    key: "textureSize",
    label: "Texture Size",
    getIsSupported: (c) => (c.textureResolutions?.length ?? 0) > 0,
  },
];

export const getCapabilityConfig = (modality: Modality): CapabilityConfig[] => {
  switch (modality) {
    case "VIDEO":
      return VIDEO_CAPABILITY_CONFIG;
    case "AUDIO":
      return AUDIO_CAPABILITY_CONFIG;
    case "THREE_D":
      return THREE_D_CAPABILITY_CONFIG;
    case "IMAGE":
    default:
      return IMAGE_CAPABILITY_CONFIG;
  }
};

const hasGuidanceFileType = (
  capabilities: StyleCapabilities,
  type: GuidanceFileType,
  role?: string,
): boolean => {
  if (!Array.isArray(capabilities.guidanceFileTypes)) {
    return false;
  }
  return capabilities.guidanceFileTypes.some(
    (ft) =>
      ft.type === type &&
      (role === undefined ||
        ((ft as { role?: string }).role ?? "REFERENCE") === role),
  );
};
